import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { inr, num } from "@/lib/api";
import { PageHeader, StatCard, Panel, Loading, CHART_COLORS } from "@/components/kit";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";
import { Wallet, TrendingUp, Boxes, Users, FileStack, AlertTriangle, CheckSquare, Banknote } from "lucide-react";

export default function Executive() {
  const { data, isLoading } = useQuery({
    queryKey: ["dash-exec"],
    queryFn: async () => (await api.get("/dashboard/executive")).data,
  });

  if (isLoading || !data) return <Loading label="Loading executive dashboard…" />;
  const k = data.kpis;

  return (
    <div>
      <PageHeader title="Executive Dashboard"
        subtitle="Group-wide incentive accrual, settlement and volume performance across ACC & ACL" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testid="kpi-accrued" label="Total Accrued" value={inr(k.total_accrued)} icon={Wallet} tone="navy" sub="Across all active schemes" />
        <StatCard testid="kpi-settled" label="Total Settled" value={inr(k.total_settled)} icon={Banknote} tone="emerald" sub="Net of TDS" />
        <StatCard testid="kpi-volume" label="Sales Volume" value={`${num(k.total_volume_mt)} MT`} icon={Boxes} tone="gold" sub="9-month window" />
        <StatCard testid="kpi-dealers" label="Active Dealers" value={num(k.active_dealers)} icon={Users} tone="violet" sub={`of ${num(k.total_dealers)} total`} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testid="kpi-schemes" label="Active Schemes" value={num(k.active_schemes)} icon={FileStack} tone="white" />
        <StatCard testid="kpi-exceptions" label="Open Exceptions" value={num(k.open_exceptions)} icon={AlertTriangle} tone="white" />
        <StatCard testid="kpi-approvals" label="Pending Approvals" value={num(k.pending_approvals)} icon={CheckSquare} tone="white" />
        <StatCard testid="kpi-settle-ratio" label="Settled Ratio"
          value={`${Math.round((k.total_settled / (k.total_accrued || 1)) * 100)}%`} icon={TrendingUp} tone="white" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <Panel testid="panel-volume-trend" title="Sales Volume Trend" subtitle="Monthly dispatched MT" className="lg:col-span-2">
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={data.volume_trend} margin={{ left: -10, right: 10 }}>
              <defs>
                <linearGradient id="vol" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#00529B" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#00529B" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
              <Tooltip formatter={(v) => `${num(v)} MT`} />
              <Area type="monotone" dataKey="volume" stroke="#00529B" strokeWidth={2.5} fill="url(#vol)" />
            </AreaChart>
          </ResponsiveContainer>
        </Panel>

        <Panel testid="panel-engine-split" title="Accrual by Engine" subtitle="Incentive by calculation type">
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={data.engine_split} dataKey="amount" nameKey="engine" innerRadius={55} outerRadius={95} paddingAngle={3}>
                {data.engine_split.map((e, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => inr(v)} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <Panel testid="panel-opco-accrued" title="Accrual by Operating Company" subtitle="ACC vs ACL incentive accrual">
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data.opco_accrued} margin={{ left: 10, right: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
            <XAxis dataKey="opco" tick={{ fontSize: 12, fill: "#334155" }} />
            <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => `${Math.round(v / 100000)}L`} />
            <Tooltip formatter={(v) => inr(v)} />
            <Bar dataKey="accrued" radius={[8, 8, 0, 0]} barSize={90}>
              {data.opco_accrued.map((e, i) => <Cell key={i} fill={i === 0 ? "#0F2C59" : "#00A3E0"} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Panel>
    </div>
  );
}
